import { Table, Button, Row, Col, Form, Pagination } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Message from '../../components/Message';
import Loader from '../../components/Loader';
import { useGetTransfersQuery, useDeleteTransferMutation } from '../../slices/transfersApiSlice';
import { toast } from 'react-toastify';
import { useState } from 'react';
import * as XLSX from 'xlsx';

const TransferListScreen = () => {
  // States
  const [filter, setFilter] = useState('');
  const [refineFilter, setRefineFilter] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Fetch data
  const { data, isLoading, error, refetch } = useGetTransfersQuery(pageNumber);
  const [deleteTransfer, { isLoading: loadingDelete }] = useDeleteTransferMutation();

  // Delete handler
  const deleteHandler = async (id) => {
    if (window.confirm('Are you sure you want to delete this transfer?')) {
      try {
        await deleteTransfer(id).unwrap();
        toast.success('Transfer deleted successfully!');
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || 'Failed to delete transfer.');
      }
    }
  };

  // ORIGINAL filtering logic (unchanged)
  const filterTransfers = () => {
    if (!data?.transfers) return [];

    return data.transfers.filter((transfer) => {
      const transferDate = new Date(transfer?.date || new Date());
      const isWithinDateRange =
        (!startDate || transferDate >= new Date(startDate)) &&
        (!endDate || transferDate <= new Date(endDate));

      const lowerFilter = filter.toLowerCase();

      return (
        isWithinDateRange &&
        (
          transfer?.note?.toLowerCase().includes(lowerFilter) ||
          transfer?.customerDetails?.name?.toLowerCase().includes(lowerFilter) ||
          transfer?.transferItems?.some((item) =>
            item?.name?.toLowerCase().includes(lowerFilter)
          )
        )
      );
    });
  };

  // SECOND FILTER: refine by product + note + patient
  const refineTransferItems = (transfer) => {
    const lower = refineFilter.toLowerCase();

    const matchesNote = transfer.note?.toLowerCase().includes(lower);
    const matchesPatient = transfer.customerDetails?.name?.toLowerCase().includes(lower);

    const matchesItems = transfer.transferItems.filter((i) =>
      i.name.toLowerCase().includes(lower)
    );

    if (matchesNote || matchesPatient) return transfer.transferItems;

    return matchesItems;
  };

  // Totals
  const calculateTotalTransfers = () => {
    return filterTransfers().reduce((total, transfer) => {
      const refinedItems = refineTransferItems(transfer);

      const transferTotal = refinedItems.reduce(
        (subTotal, item) => subTotal + item.qty * item.price,
        0
      );

      return total + transferTotal;
    }, 0);
  };

  const calculateTotalQty = () => {
    return filterTransfers().reduce((totalQty, transfer) => {
      const refinedItems = refineTransferItems(transfer);

      return totalQty + refinedItems.reduce((sum, item) => sum + item.qty, 0);
    }, 0);
  };

  // ─── Excel Export ─────────────────────────────────────────────────────────
  const exportToExcel = () => {
    const rows = filterTransfers().flatMap((transfer) => {
      const refinedItems = refineTransferItems(transfer);

      return refinedItems.map((i) => ({
        Date: new Date(transfer.date).toLocaleDateString(),
        Product: i.name,
        Quantity: i.qty,
        Total: (i.qty * i.price).toFixed(2),
        Note: transfer.note || 'N/A',
        Patient: transfer.customerDetails?.name || 'N/A'
      }));
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Transfers');
    XLSX.writeFile(wb, 'Transfers_Report.xlsx');
  };

  // Pagination
  const paginationHandler = (page) => setPageNumber(page);

  return (
    <>
      <Row className="align-items-center mb-3">
        <Col>
          <h1>Transfers</h1>

          <Button
            variant="success"
            className="my-3 mx-2"
            onClick={exportToExcel}
          >
            Export to Excel
          </Button>

          <Link to='/admin/transfersearchReplace' className='btn btn-light my-3'>
            Search
          </Link>
          <Link to="/admin/transfersearchdelete" className="btn btn-light">
          Del
          </Link>
        </Col>

        <Col md={3}>
          <Form.Control
            type="text"
            placeholder="Filter by product, note, or patient"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>

        <Col md={3}>
          <Form.Control
            type="text"
            placeholder="Refine by product, note, or patient"
            value={refineFilter}
            onChange={(e) => setRefineFilter(e.target.value)}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Col>

        <Col md={3}>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Col>

        <Col md={6} className="text-end">
          <h4>Total Transfers: ${calculateTotalTransfers().toFixed(2)}</h4>
          <h4>Total Qty: {calculateTotalQty().toFixed(2)}</h4>
        </Col>
      </Row>

      {loadingDelete && <Loader />}

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error?.data?.message || 'An error occurred'}</Message>
      ) : filterTransfers().length === 0 ? (
        <Message>No transfers match your criteria.</Message>
      ) : (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th>DATE</th>
                <th>PRODUCT</th>
                <th>QTY</th>
                <th>TOTAL</th>
                <th>NOTE</th>
                <th>PATIENT</th>
                <th>IMAGE</th>
                <th>ACTIONS</th>
              </tr>
            </thead>

            <tbody>
              {filterTransfers().map((transfer) => {
                const refinedItems = refineTransferItems(transfer);

                return refinedItems.map((item) => (
                  <tr key={`${transfer?._id}-${item?.name}`}>
                    <td>{new Date(transfer?.date).toLocaleDateString()}</td>
                    <td>{item?.name}</td>
                    <td>{item?.qty}</td>
                    <td>${(item?.qty * item?.price).toFixed(2)}</td>
                    <td>{transfer?.note || 'N/A'}</td>
                    <td>{transfer?.customerDetails?.name || 'N/A'}</td>
                    <td>
                      {item?.images?.[0] ? (
                        <img
                          src={item.images[0]}
                          alt="Thumbnail"
                          style={{
                            width: '10px',
                            height: '10px',
                            objectFit: 'cover',
                            borderRadius: '5px',
                          }}
                        />
                      ) : (
                        'No image'
                      )}
                    </td>
                    <td>
                      <Button
                        as={Link}
                        to={`/transfer/${transfer?._id}`}
                        variant="light"
                        className="btn-sm"
                      >
                        Details
                      </Button>

                      <Button
                        as={Link}
                        to={`/admin/transfer/${transfer?._id}/edit`}
                        variant="light"
                        className="btn-sm mx-2"
                      >
                        Edit
                      </Button>
                      <Button
                        as={Link}
                        to={`/admin/transfer/${transfer?._id}/copy`}
                        variant="light"
                        className="btn-sm mx-2"
                      >
                        Copy
                      </Button>

                      <Button
                        variant="danger"
                        className="btn-sm mx-2"
                        onClick={() => deleteHandler(transfer?._id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ));
              })}
            </tbody>
          </Table>

          <Pagination>
            {[...Array(data.pages).keys()].map((x) => (
              <Pagination.Item
                key={x + 1}
                active={x + 1 === pageNumber}
                onClick={() => paginationHandler(x + 1)}
              >
                {x + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </>
      )}
    </>
  );
};

export default TransferListScreen;
